// Per-point GPU inference pipeline with double-buffered async readback.
// Port of gpu_inference.rs.
//
// Key difference from native: Rust uses synchronous device.poll(Wait),
// WebGPU uses async mapAsync. We use a readbackReady flag set by the
// .then() callback. If previous readback isn't consumed yet, we skip
// dispatching new work (no lost results, just slower throughput).

import type { TileModelBuffers, TileWeights, InferParams } from './types';
import { INFER_BUDGET } from './types';
import { f32ToF16 } from './math';

export class GpuInference {
  private pipeline: GPUComputePipeline;
  private bindGroupLayout: GPUBindGroupLayout;

  constructor(device: GPUDevice, shaderCode: string) {
    const module = device.createShaderModule({ code: shaderCode });

    this.bindGroupLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: 'unfilterable-float', viewDimension: '2d' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, sampler: { type: 'non-filtering' } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
      ],
    });

    const pipelineLayout = device.createPipelineLayout({
      bindGroupLayouts: [this.bindGroupLayout],
    });

    this.pipeline = device.createComputePipeline({
      layout: pipelineLayout,
      compute: { module, entryPoint: 'main' },
    });
  }

  createModelBuffers(
    device: GPUDevice,
    queue: GPUQueue,
    weights: TileWeights,
    outputRes: number,
    initialData: Uint8Array,
  ): TileModelBuffers {
    const weightsBuf = device.createBuffer({
      size: weights.mlpWeights.byteLength,
      usage: GPUBufferUsage.STORAGE,
      mappedAtCreation: true,
    });
    new Float32Array(weightsBuf.getMappedRange()).set(weights.mlpWeights);
    weightsBuf.unmap();

    const freqsBuf = device.createBuffer({
      size: weights.freqs.byteLength,
      usage: GPUBufferUsage.STORAGE,
      mappedAtCreation: true,
    });
    new Float32Array(freqsBuf.getMappedRange()).set(weights.freqs);
    freqsBuf.unmap();

    // Grid texture: R8Unorm
    const gridTex = device.createTexture({
      size: { width: weights.gridRes, height: weights.gridRes },
      format: 'r8unorm',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    });
    queue.writeTexture(
      { texture: gridTex },
      weights.gridData,
      { bytesPerRow: weights.gridRes, rowsPerImage: weights.gridRes },
      { width: weights.gridRes, height: weights.gridRes },
    );
    const gridView = gridTex.createView();
    const gridSampler = device.createSampler({
      magFilter: 'nearest',
      minFilter: 'nearest',
    });

    const elevRange = weights.elevMax - weights.elevMin;
    const baseParams: InferParams = {
      outputRes,
      nCoords: 0,
      gridMin: weights.gridMin,
      gridScale: weights.gridScale,
      elevMin: weights.elevMin,
      elevRange,
      residualScale: 0.3,
      coordScale: 1.0,
      coordOffsetU: 0.0,
      coordOffsetV: 0.0,
    };

    const paramsBuf = device.createBuffer({
      size: 48,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    queue.writeBuffer(paramsBuf, 0, encodeInferParams(baseParams));

    const coordsBuf = device.createBuffer({
      size: INFER_BUDGET * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    const outBuf = device.createBuffer({
      size: INFER_BUDGET * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    });

    const readbackBufs: [GPUBuffer, GPUBuffer] = [
      device.createBuffer({ size: INFER_BUDGET * 4, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST }),
      device.createBuffer({ size: INFER_BUDGET * 4, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST }),
    ];

    const dirtyWords = Math.ceil(outputRes / 64);

    const bindGroup = device.createBindGroup({
      layout: this.bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: weightsBuf } },
        { binding: 1, resource: { buffer: freqsBuf } },
        { binding: 2, resource: gridView },
        { binding: 3, resource: gridSampler },
        { binding: 4, resource: { buffer: coordsBuf } },
        { binding: 5, resource: { buffer: outBuf } },
        { binding: 6, resource: { buffer: paramsBuf } },
      ],
    });

    return {
      weightsBuf,
      freqsBuf,
      gridTex,
      gridView,
      gridSampler,
      paramsBuf,
      coordsBuf,
      outBuf,
      readbackBufs,
      readbackFront: 0,
      readbackReady: false,
      pendingCoords: null,
      pendingDiscard: false,
      bindGroup,
      shadowData: new Uint8Array(initialData),
      dirtyRows: new BigUint64Array(dirtyWords),
      outputRes,
      baseParams,
    };
  }

  /**
   * Try to consume previous readback, then dispatch new inference.
   *
   * Returns the coords that were successfully scattered (from the PREVIOUS
   * frame's dispatch), or null if nothing was consumed. The caller uses this
   * to mark occupancy only for coords whose results actually landed.
   *
   * If the previous readback hasn't completed yet, does NOT dispatch new
   * work — returns null and the caller should skip this frame. This ensures
   * we never lose results or write to a mapped buffer.
   */
  dispatchAndScatter(
    device: GPUDevice,
    queue: GPUQueue,
    model: TileModelBuffers,
    coords: Uint32Array,
  ): Uint32Array | null {
    let consumed: Uint32Array | null = null;

    // 1. Try to consume previous readback
    if (model.readbackReady && model.pendingCoords) {
      const prevFront = 1 - model.readbackFront;
      const readbackBuf = model.readbackBufs[prevFront];

      if (model.pendingDiscard) {
        // Results are stale (e.g. detail patch slid) — unmap but don't scatter
        readbackBuf.unmap();
        model.pendingDiscard = false;
      } else {
        const data = new Uint32Array(readbackBuf.getMappedRange());
        this.scatterResults(model, model.pendingCoords, data);
        readbackBuf.unmap();
        consumed = model.pendingCoords;
      }

      model.pendingCoords = null;
      model.readbackReady = false;
    }

    // 2. If previous readback still pending, skip this frame's dispatch
    if (model.pendingCoords) {
      return consumed;
    }

    if (coords.length === 0) return consumed;

    const nCoords = coords.length;

    // 3. Write coords + params
    queue.writeBuffer(model.coordsBuf, 0, coords);
    const params: InferParams = { ...model.baseParams, nCoords };
    queue.writeBuffer(model.paramsBuf, 0, encodeInferParams(params));

    // 4. Compute pass + copy to readback buffer
    const encoder = device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, model.bindGroup);
    pass.dispatchWorkgroups(Math.ceil(nCoords / 64), 1, 1);
    pass.end();

    const front = model.readbackFront;
    const copySize = nCoords * 4;
    encoder.copyBufferToBuffer(model.outBuf, 0, model.readbackBufs[front], 0, copySize);
    queue.submit([encoder.finish()]);

    // 5. Initiate async readback — the .then() sets readbackReady
    // Copy coords — caller may reuse the backing buffer next frame
    model.pendingCoords = new Uint32Array(coords);
    model.readbackBufs[front].mapAsync(GPUMapMode.READ, 0, copySize).then(
      () => { model.readbackReady = true; },
      () => {
        // mapAsync failed — drop this batch, clear pending so we can retry
        model.pendingCoords = null;
        model.readbackReady = false;
      },
    );

    // 6. Swap front buffer for next frame
    model.readbackFront = 1 - front;

    return consumed;
  }

  private scatterResults(model: TileModelBuffers, coords: Uint32Array, results: Uint32Array): void {
    const res = model.outputRes;
    const rowBytes = res * 2;

    for (let i = 0; i < coords.length; i++) {
      const packed = coords[i];
      const px = packed >>> 16;
      const py = packed & 0xffff;

      // Extract low 16 bits = f16 value
      const f16Lo = results[i] & 0xffff;
      const offset = py * rowBytes + px * 2;
      model.shadowData[offset] = f16Lo & 0xff;
      model.shadowData[offset + 1] = (f16Lo >>> 8) & 0xff;

      // Mark row dirty
      const word = (py / 64) | 0;
      const bit = py % 64;
      model.dirtyRows[word] |= 1n << BigInt(bit);
    }
  }

  /** Upload dirty rows from shadow buffer to texture, clear dirty flags. */
  flushDirtyRows(queue: GPUQueue, model: TileModelBuffers, texture: GPUTexture): void {
    const res = model.outputRes;
    const rowBytes = res * 2;

    for (let wordIdx = 0; wordIdx < model.dirtyRows.length; wordIdx++) {
      const bits = model.dirtyRows[wordIdx];
      if (bits === 0n) continue;

      for (let bit = 0; bit < 64; bit++) {
        if ((bits & (1n << BigInt(bit))) === 0n) continue;
        const row = wordIdx * 64 + bit;
        if (row >= res) break;

        const offset = row * rowBytes;
        const rowData = model.shadowData.subarray(offset, offset + rowBytes);

        queue.writeTexture(
          { texture, origin: { x: 0, y: row, z: 0 } },
          rowData,
          { bytesPerRow: res * 2, rowsPerImage: 1 },
          { width: res, height: 1 },
        );
      }
    }

    model.dirtyRows.fill(0n);
  }
}

// Reusable scratch buffer for inference params — avoids per-dispatch allocation
const _inferParamsBuf = new ArrayBuffer(48);
const _inferParamsU32 = new Uint32Array(_inferParamsBuf);
const _inferParamsF32 = new Float32Array(_inferParamsBuf);

function encodeInferParams(p: InferParams): ArrayBuffer {
  _inferParamsU32[0] = p.outputRes;
  _inferParamsU32[1] = p.nCoords;
  _inferParamsF32[2] = p.gridMin;
  _inferParamsF32[3] = p.gridScale;
  _inferParamsF32[4] = p.elevMin;
  _inferParamsF32[5] = p.elevRange;
  _inferParamsF32[6] = p.residualScale;
  _inferParamsF32[7] = p.coordScale;
  _inferParamsF32[8] = p.coordOffsetU;
  _inferParamsF32[9] = p.coordOffsetV;
  _inferParamsU32[10] = 0; // _pad2.x
  _inferParamsU32[11] = 0; // _pad2.y
  return _inferParamsBuf;
}

/**
 * MLP layer shapes: [rows, cols] for weights, [rows] for biases.
 * Order must match export_gpu_weights.py weight_bias_order.
 */
const MLP_LAYERS: Array<[number, number]> = [
  [16, 512],    // film_w1
  [768, 16],    // film_w2
  [192, 512],   // trunk0_w
  [192, 192],   // trunk1_w
  [1, 192],     // out_w
];

const MLP_BIASES: number[] = [16, 768, 192, 192, 1];

/**
 * Dequantize int8 weights + per-channel f32 scales back to f32.
 * Each row i: f32[j] = int8[j] * scales[i]
 */
function dequantizeInt8(
  int8Data: Int8Array,
  scales: Float32Array,
  rows: number,
  cols: number,
): Float32Array {
  const out = new Float32Array(rows * cols);
  for (let r = 0; r < rows; r++) {
    const s = scales[r];
    const rowOff = r * cols;
    for (let c = 0; c < cols; c++) {
      out[rowOff + c] = int8Data[rowOff + c] * s;
    }
  }
  return out;
}

/** Parse a .weights binary file into TileWeights. */
export function parseTileWeights(data: ArrayBuffer): import('./types').TileWeights {
  const view = new DataView(data);

  const tileId = view.getUint32(0, true);
  const gridRes = view.getUint32(4, true);
  const gridMin = view.getFloat32(8, true);
  const gridMax = view.getFloat32(12, true);
  const gridScale = view.getFloat32(16, true);
  const elevMin = view.getFloat32(20, true);
  const elevMax = view.getFloat32(24, true);
  const fmtVer = view.getUint32(28, true);

  const headerEnd = 32;
  const gridSize = gridRes * gridRes;
  const gridEnd = headerEnd + gridSize;
  const gridData = new Uint8Array(data, headerEnd, gridSize);

  const numFreqs = 128;
  const freqsEnd = gridEnd + numFreqs * 4;
  const freqs = new Float32Array(data, gridEnd, numFreqs);

  let mlpWeights: Float32Array;

  if (fmtVer === 1) {
    // Int8 format: for each layer, read int8 weights + f32 scales, then f32 bias
    const parts: Float32Array[] = [];
    let offset = freqsEnd;
    for (let i = 0; i < MLP_LAYERS.length; i++) {
      const [rows, cols] = MLP_LAYERS[i];
      const biasLen = MLP_BIASES[i];

      // int8 weight data
      const wBytes = rows * cols;
      const int8Data = new Int8Array(data, offset, wBytes);
      offset += wBytes;

      // f32 per-channel scales
      const scales = new Float32Array(data, offset, rows);
      offset += rows * 4;

      // Dequantize to f32
      const dequantized = dequantizeInt8(int8Data, scales, rows, cols);
      parts.push(dequantized);

      // f32 bias
      const bias = new Float32Array(data, offset, biasLen);
      parts.push(bias);
      offset += biasLen * 4;

    }

    // Concatenate all parts into a single Float32Array
    const totalFloats = parts.reduce((s, p) => s + p.length, 0);
    mlpWeights = new Float32Array(totalFloats);
    let pos = 0;
    for (const part of parts) {
      mlpWeights.set(part, pos);
      pos += part.length;
    }
  } else {
    // Format version 0: all f32
    const mlpStart = freqsEnd;
    const mlpFloats = (data.byteLength - mlpStart) / 4;
    mlpWeights = new Float32Array(data, mlpStart, mlpFloats);
  }

  return { tileId, gridRes, gridMin, gridMax, gridScale, elevMin, elevMax, gridData, freqs, mlpWeights };
}

/** CPU bilinear upsample LOD0 (f16 bytes) to hires resolution (f16 bytes). */
// Precomputed f16→f32 lookup table (64KB, built once)
let _f16Lut: Float32Array | null = null;
function getF16Lut(): Float32Array {
  if (_f16Lut) return _f16Lut;
  _f16Lut = new Float32Array(65536);
  for (let i = 0; i < 65536; i++) {
    _f16Lut[i] = f16ToF32Bits(i);
  }
  return _f16Lut;
}

/** Nearest-neighbor upsample — blocky/pixelated look, ~2ms. */
export function upsampleLod0Nearest(lod0Data: Uint8Array, lod0Res: number, hiresRes: number): Uint8Array {
  const src = new Uint16Array(lod0Data.buffer, lod0Data.byteOffset, lod0Data.byteLength / 2);
  const dst = new Uint16Array(hiresRes * hiresRes);
  const scale = lod0Res / hiresRes;
  for (let y = 0; y < hiresRes; y++) {
    const sy = Math.min(lod0Res - 1, (y * scale) | 0);
    const srcRow = sy * lod0Res;
    const dstRow = y * hiresRes;
    for (let x = 0; x < hiresRes; x++) {
      dst[dstRow + x] = src[srcRow + (Math.min(lod0Res - 1, (x * scale) | 0))];
    }
  }
  return new Uint8Array(dst.buffer);
}

/** Bilinear upsample — smooth interpolation, ~5-10ms. */
export function upsampleLod0ToHires(lod0Data: Uint8Array, lod0Res: number, hiresRes: number): Uint8Array {
  const src = new Uint16Array(lod0Data.buffer, lod0Data.byteOffset, lod0Data.byteLength / 2);
  const dst = new Uint16Array(hiresRes * hiresRes);
  const scale = lod0Res / hiresRes;
  const lut = getF16Lut();

  // Precompute per-axis: source indices and fractional weights
  const xIdx0 = new Uint16Array(hiresRes);
  const xIdx1 = new Uint16Array(hiresRes);
  const xFrac = new Float32Array(hiresRes);
  for (let x = 0; x < hiresRes; x++) {
    const sx = (x + 0.5) * scale - 0.5;
    const i0 = Math.max(0, sx | 0);
    xIdx0[x] = Math.min(i0, lod0Res - 1);
    xIdx1[x] = Math.min(i0 + 1, lod0Res - 1);
    xFrac[x] = sx - (sx | 0);
  }

  // Fast f32→f16 via bit manipulation (no Math.log2/pow, handles normal range 0–1)
  const _f2hBuf = new Float32Array(1);
  const _f2hU32 = new Uint32Array(_f2hBuf.buffer);
  const fastF32ToF16 = (v: number): number => {
    _f2hBuf[0] = v;
    const b = _f2hU32[0];
    const sign = (b >>> 16) & 0x8000;
    const expo = ((b >>> 23) & 0xff) - 127 + 15;
    if (expo <= 0) return sign;
    if (expo >= 31) return sign | 0x7c00;
    return sign | (expo << 10) | ((b >>> 13) & 0x3ff);
  };

  for (let y = 0; y < hiresRes; y++) {
    const sy = (y + 0.5) * scale - 0.5;
    const j0 = Math.max(0, sy | 0);
    const y0 = Math.min(j0, lod0Res - 1);
    const y1 = Math.min(j0 + 1, lod0Res - 1);
    const fy = sy - (sy | 0);
    const fy1 = 1 - fy;
    const srcRow0 = y0 * lod0Res;
    const srcRow1 = y1 * lod0Res;
    const dstRow = y * hiresRes;

    for (let x = 0; x < hiresRes; x++) {
      const ix0 = xIdx0[x], ix1 = xIdx1[x];
      const fx = xFrac[x];
      const val = (lut[src[srcRow0 + ix0]] * (1 - fx) + lut[src[srcRow0 + ix1]] * fx) * fy1 +
                  (lut[src[srcRow1 + ix0]] * (1 - fx) + lut[src[srcRow1 + ix1]] * fx) * fy;
      dst[dstRow + x] = fastF32ToF16(val);
    }
  }

  return new Uint8Array(dst.buffer);
}

function f16ToF32Bits(bits: number): number {
  const sign = (bits >>> 15) & 1;
  const exp = (bits >>> 10) & 0x1f;
  const frac = bits & 0x3ff;
  if (exp === 0) {
    if (frac === 0) return sign ? -0 : 0;
    return (sign ? -1 : 1) * (frac / 1024) * Math.pow(2, -14);
  }
  if (exp === 0x1f) return frac ? NaN : (sign ? -Infinity : Infinity);
  return (sign ? -1 : 1) * Math.pow(2, exp - 15) * (1 + frac / 1024);
}
